/* eslint-disable react-hooks/rules-of-hooks */
import { Check, Close, ErrorOutline, WarningAmberOutlined, WbIncandescentOutlined } from '@mui/icons-material';
import clsx from 'clsx';
import React, { HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAnimationFrame } from '../../hooks/useAnimationFrame';
import { useToast } from '../../hooks/useToast';

export type ToastProps = {
	open?: boolean;
	message: string | React.ReactNode | (({ close, setPaused }: { close: () => void; setPaused: (paused: boolean) => void }) => React.ReactNode);
	status: 'success' | 'info' | 'warning' | 'error';
	position?: 'top-right' | 'bottom-left';
	closeable?: boolean;
	onClose?: () => void;
	// Set icon to null to hide the default icons based on status
	icon?: React.ReactNode | null;
	timeout?: number; // seconds
	showCountdown?: boolean;
	// Render toast directly in the DOM
	inline?: boolean;
	inlineAnchor?: {
		vertical: 'top' | 'bottom';
		horizontal: 'left' | 'center' | 'right';
	};
};

export const ToastDefaults = {
	position: 'top-right' as const,
	closeable: true,
};

export function Toast(props: ToastProps & HTMLAttributes<HTMLDivElement>) {
	const propsWithDefaults = { ...ToastDefaults, ...props };
	const {
		open,
		message,
		status,
		closeable,
		onClose,
		icon,
		timeout,
		showCountdown,
		inline,
		inlineAnchor,
		...rest
	} = propsWithDefaults;

	if (inline !== true) {
		// By default, treat toast as a headless component and render toasts through useToast and ToastProvider instead of directly in the DOM
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const [, setToastState] = useToast(props);

		useEffect(() => {
			setToastState((prev) => ({ ...prev, ...props }));
		}, [props, setToastState]);

		return null;
	} else {
		const [visible, setVisible] = useState(open);
		const [countdown, setCountdown] = useState(timeout);
		const [hovered, setHovered] = useState(false);
		const [focused, setFocused] = useState(false);
		const [forcePaused, setForcePaused] = useState(false);
		const paused = forcePaused || hovered || focused;

		const close = () => {
			setVisible(false);
			onClose?.();
		};

		useAnimationFrame(({ delta }) => {
			if (!timeout || paused) return;
			setCountdown((prev) => prev - delta);
			if (countdown <= 0) {
				close();
			}
		});

		useEffect(() => {
			setCountdown(timeout);
		}, [timeout]);

		return (
			<div
				className={clsx(
					'group relative inline-flex flex-row items-start gap-3 z-[var(--z-toast)] max-w-[500px]',
					'border rounded-md shadow-md px-4 py-3',
					'font-normal text-sm',
					!visible && 'hidden',
					{
						'fixed! m-4': inlineAnchor,
						'top-0': inlineAnchor?.vertical === 'top',
						'bottom-0': inlineAnchor?.vertical === 'bottom',
						'left-0': inlineAnchor?.horizontal === 'left',
						'right-0': inlineAnchor?.horizontal === 'right',
						'mx-auto': inlineAnchor?.horizontal === 'center',
					},
					{
						'bg-success-green-5 border-success-green-60 text-success-green-80': status === 'success',
						'bg-warning-yellow-5 border-warning-yellow-70 text-warning-yellow-80': status === 'warning',
						'bg-failure-red-5 border-failure-red-60 text-failure-red-80': status === 'error',
						'bg-success-blue-5 border-success-blue-60 text-success-blue-80': status === 'info',
					},
				)}
				onMouseOver={() => setHovered(true)}
				onMouseLeave={() => setHovered(false)}
				onFocus={() => setFocused(true)}
				onBlur={() => setFocused(false)}
				{...rest}
			>
				{icon}
				{icon === undefined && (
					<>
						{status === 'success' && <Check />}
						{status === 'warning' && <WarningAmberOutlined />}
						{status === 'error' && <ErrorOutline />}
						{status === 'info' && <WbIncandescentOutlined className="rotate-180" />}
					</>
				)}
				<div className="mt-auto mb-auto min-w-0">
					{message instanceof Function ? message({ close, setPaused: setForcePaused }) : message}
				</div>
				{closeable && (
					<button onClick={close} className="cursor-pointer"><Close /></button>
				)}
				{timeout && showCountdown && (
					<div
						className={clsx(
							'absolute bottom-0 left-[3px] right-[3px] h-[1px]', // Can't use overflow-hidden to clip the bar to the rounded borders cause that messes up auto-animate for some reason
							{
								'bg-success-green-80': status === 'success',
								'bg-warning-yellow-80': status === 'warning',
								'bg-failure-red-80': status === 'error',
								'bg-success-blue-80': status === 'info',
							},
						)}
						style={{ width: `${countdown / timeout * 100}%` }}
					/>
				)}
			</div>
		);
	}
}
